/* eslint-disable insio-custom/no-js-date -- react-day-picker API requires JS Date at the boundary */
'use client';
import * as React from 'react';
import { flushSync } from 'react-dom';
import { Input } from './inputs/Input';
import { DayPicker, DateRange } from 'react-day-picker';
import { Popover, PopoverButton, PopoverPanel, Transition } from '@headlessui/react';
import { Icon } from './icons/Icon';
import { cs, enUS, de, sv, fr, frCH } from 'date-fns/locale';
import 'react-day-picker/dist/style.css';
import { RangeDate } from '../types/RangeDate';
import type { Locale } from 'date-fns';
import { cn } from '../utils';
import { adjacentTabbable } from '../utils/adjacent-tabbable';
import { GLOBAL_HEIGHT, GlobalSize } from '../types/global-size';
import { nowPlainDate, Temporal } from '@insio/dates';
import { useLocale } from '../providers/UIProvider';
import { useTranslation } from '../providers/UIProvider';

// This file is lightly documented for the sake of its complexity

// ─── Boundary conversion helpers ─────────────────────────────────────────────
// react-day-picker works with JS Date internally; the public API uses
// Temporal.PlainDate.  These helpers convert at the boundary.

function toJSDate(pd: Temporal.PlainDate): Date {
  return new Date(pd.year, pd.month - 1, pd.day);
}

// Years the month/year dropdowns offer before and after today.
const DROPDOWN_YEARS_BACK = 100;
const DROPDOWN_YEARS_AHEAD = 20;

function toPlainDate(d: Date): Temporal.PlainDate {
  return Temporal.PlainDate.from({
    year: d.getFullYear(),
    month: d.getMonth() + 1,
    day: d.getDate()
  });
}

/**
 * The string used to join the two dates of a range in the text input.
 * Must contain at least one space on each side so it cannot be confused with
 * hyphens that appear inside ISO date strings (e.g. "2025-11-19").
 */
const RANGE_SEPARATOR = ' - ';

/**
 * Maps BCP-47 locale prefixes to date-fns Locale objects for react-day-picker.
 * react-day-picker requires date-fns Locale objects — this is a library constraint.
 */
const DAY_PICKER_LOCALE_MAP: Record<string, Locale> = {
  en: enUS,
  fr: fr,
  'fr-CH': frCH,
  de: de,
  sv: sv,
  cs: cs
};

function getDayPickerLocale(locale: string): Locale {
  // Exact match first (e.g. 'fr-CH'), then prefix (e.g. 'en-US' → 'en')
  return DAY_PICKER_LOCALE_MAP[locale] ?? DAY_PICKER_LOCALE_MAP[locale.split('-')[0]] ?? cs;
}

/** Formats a JS Date for display using the given BCP-47 locale. */
function formatDate(d: Date, locale: string): string {
  return toPlainDate(d).toLocaleString(locale, {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric'
  });
}

/**
 * Tries to parse a date string in common formats (d.M.yyyy, M/d/yyyy, yyyy-MM-dd).
 * Returns a JS Date if valid, undefined otherwise. No date-fns dependency needed.
 */
function tryParseDate(input: string): Date | undefined {
  const clean = input.replace(/\s/g, '').replace(/\u200B/g, '');

  let year: number, month: number, day: number;

  const dotMatch = clean.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  const slashMatch = clean.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  const isoMatch = clean.match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (dotMatch) {
    day = Number(dotMatch[1]);
    month = Number(dotMatch[2]);
    year = Number(dotMatch[3]);
  } else if (slashMatch) {
    month = Number(slashMatch[1]);
    day = Number(slashMatch[2]);
    year = Number(slashMatch[3]);
  } else if (isoMatch) {
    year = Number(isoMatch[1]);
    month = Number(isoMatch[2]);
    day = Number(isoMatch[3]);
  } else {
    return undefined;
  }

  if (month < 1 || month > 12 || day < 1 || day > 31) return undefined;

  const d = new Date(year, month - 1, day);
  // Validate that the date didn't overflow (e.g. Feb 30 → Mar 2)
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) {
    return undefined;
  }

  return d;
}

/** Internal range type using JS Date for react-day-picker compatibility. */
interface InternalRangeDate {
  from: Date | undefined;
  to?: Date | undefined;
}

export interface DatepickerProps {
  id?: string;
  mode: 'single' | 'range';
  withPresets?: boolean;
  withConfirmButtons?: boolean;
  MonthRange?: 1 | 2 | 4 | 6 | 12;
  value?: RangeDate | Temporal.PlainDate;
  onChange?: (value: Temporal.PlainDate | RangeDate | undefined) => void;
  closeOnConfirm?: boolean;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  className?: string;
  placeholder?: string;
  hasError?: boolean;
  triggerAnchor?: 'left' | 'right';
  disabled?: boolean;
  disableBefore?: Temporal.PlainDate;
  disableAfter?: Temporal.PlainDate;
  height?: GlobalSize;
  autoFocus?: boolean;
  testId?: string;
  /** Show the calendar popover open on mount. Useful when rendering the Datepicker as an inline editor. */
  defaultOpen?: boolean;
  /** Single mode: pick the month and year from dropdowns instead of paging month by month. */
  withMonthYearSelect?: boolean;
}

export const Datepicker = React.forwardRef<HTMLInputElement, DatepickerProps>(
  (
    {
      id,
      mode,
      MonthRange,
      withPresets = false,
      onChange,
      withConfirmButtons = false,
      value,
      closeOnConfirm = true,
      className,
      hasError,
      disabled,
      onBlur,
      height = 'md',
      triggerAnchor = 'left',
      disableAfter,
      disableBefore,
      placeholder,
      autoFocus,
      testId,
      defaultOpen = false,
      withMonthYearSelect = false
    },
    ref
  ) => {
    /*
    Helpers
   */
    const dateLocale = useLocale();
    const t = useTranslation('components.datepicker');
    const isRangeDateValue = (val: unknown): val is RangeDate =>
      !!val && typeof val === 'object' && ('from' in val || 'to' in val);

    const isDateInRange = (date: Date, range: InternalRangeDate) => {
      if (!range.from || !range.to) return false;
      return date >= range.from && date <= range.to;
    };

    const asDateRange = (range: InternalRangeDate) => {
      return range.from ? (range as DateRange) : undefined;
    };

    /** Convert an InternalRangeDate to the public RangeDate (Temporal.PlainDate). */
    const toPublicRange = (range: InternalRangeDate): RangeDate => ({
      from: range.from ? toPlainDate(range.from) : undefined,
      to: range.to ? toPlainDate(range.to) : undefined
    });
    /*
      States
    */

    // normal mode states — internal JS Date for react-day-picker
    const [singleDate, setSingleDate] = React.useState<Date | undefined>();
    const [rangeDate, setRangeDate] = React.useState<InternalRangeDate>({
      from: undefined,
      to: undefined
    });

    // confirmation button states — internal JS Date
    const [pendingSingleDate, setPendingSingleDate] = React.useState<Date | undefined>();
    const [pendingRangeDate, setPendingRangeDate] = React.useState<InternalRangeDate>({
      from: undefined,
      to: undefined
    });

    // determines how many months show
    const MonthRangeConfig = MonthRange ? MonthRange : mode === 'range' ? 2 : 1;

    // input value, ignores confirmation/normal states, works in both cases
    const [rawInputValue, setRawInputValue] = React.useState<string>('');
    // confirmation state display near the buttons
    const [displayValue, setDisplayValue] = React.useState<string>('');

    //confirm close forcing
    const popoverButtonRef = React.useRef<HTMLButtonElement>(null);
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const setInputRef = React.useCallback(
      (element: HTMLInputElement | null) => {
        inputRef.current = element;
        if (typeof ref === 'function') ref(element);
        else if (ref) ref.current = element;
      },
      [ref]
    );

    const handleConfirm = () => {
      if (popoverButtonRef.current) {
        popoverButtonRef.current.click();
      }
    };
    /*
      Switching between confirmation and range
    */
    React.useEffect(() => {
      setPendingSingleDate(undefined);
      setPendingRangeDate({ from: undefined, to: undefined });
    }, [withConfirmButtons]);

    // Open the calendar popover on mount when `defaultOpen` is set.
    // Headless UI's Popover has no controlled/open prop, so we synthesise a
    // click on the PopoverButton once after mount.
    const didAutoOpen = React.useRef(false);
    const suppressNextButtonBlur = React.useRef(false);
    React.useEffect(() => {
      if (didAutoOpen.current) return;
      if (defaultOpen && popoverButtonRef.current) {
        didAutoOpen.current = true;
        suppressNextButtonBlur.current = true;
        popoverButtonRef.current.click();
        // The click focuses the calendar button; an autofocused input takes it back for typing,
        // before the panel mounts — later, the popover would close on focus leaving it.
        if (autoFocus) inputRef.current?.focus();
      }
    }, [defaultOpen]);

    const panelRef = React.useRef<HTMLDivElement>(null);

    // Opens the calendar from the keyboard; focus stays in the input for typing.
    const openCalendar = () => {
      suppressNextButtonBlur.current = true;
      popoverButtonRef.current?.click();
      inputRef.current?.focus();
    };

    // Moves focus onto the calendar's focusable day, where its arrow keys navigate.
    const focusCalendarDay = () => {
      const days = panelRef.current;
      (
        days?.querySelector<HTMLElement>('.rdp-day_button[tabindex="0"]') ??
        days?.querySelector<HTMLElement>('.rdp-day_button')
      )?.focus();
    };

    /*
    Initial value — convert Temporal.PlainDate → JS Date at the boundary
    */
    React.useEffect(() => {
      if (!value) {
        // Parent cleared the value — reset all internal state to match.
        setSingleDate(undefined);
        setRangeDate({ from: undefined, to: undefined });
        setPendingSingleDate(undefined);
        setPendingRangeDate({ from: undefined, to: undefined });
        setRawInputValue('');
        setDisplayValue('');
        return;
      }

      if (mode === 'single') {
        /*
        Single value
        */
        if (value instanceof Temporal.PlainDate) {
          const jsDate = toJSDate(value);
          if (withConfirmButtons) {
            setPendingSingleDate(jsDate);
            return;
          }

          setSingleDate(jsDate);
          setRawInputValue(formatDate(jsDate, dateLocale));
        } else if (isRangeDateValue(value) && value.from) {
          /*
            Object passed to single mode date
          */
          const jsFrom = toJSDate(value.from);
          if (withConfirmButtons) {
            setPendingSingleDate(jsFrom);
            return;
          }

          setSingleDate(jsFrom);
          setRawInputValue(formatDate(jsFrom, dateLocale));
        }
      } else if (mode === 'range') {
        /*
            Range value
          */
        if (isRangeDateValue(value)) {
          const internalRange: InternalRangeDate = {
            from: value.from ? toJSDate(value.from) : undefined,
            to: value.to ? toJSDate(value.to) : undefined
          };
          if (withConfirmButtons) {
            setPendingRangeDate(internalRange);
            return;
          }

          setRangeDate(internalRange);

          const formattedValue = internalRange.to
            ? `${formatDate(internalRange.from!, dateLocale)} - ${formatDate(internalRange.to, dateLocale)}`
            : `${formatDate(internalRange.from!, dateLocale)}`;

          setRawInputValue(formattedValue);
        } else if (value instanceof Temporal.PlainDate) {
          /*
            A PlainDate passed to range mode, its casted as an object where from and to are the same
          */
          const jsDate = toJSDate(value);
          if (withConfirmButtons) {
            setPendingRangeDate({ from: jsDate, to: jsDate });
            return;
          }

          setRangeDate({ from: jsDate, to: jsDate });
          const formattedValue = `${formatDate(jsDate, dateLocale)} - ${formatDate(jsDate, dateLocale)}`;

          setRawInputValue(formattedValue);
        }
      }
    }, [value, mode]);

    /*
      Switching between modes
      switching between confirmation and normal can transfer values
    */
    React.useEffect(() => {
      if (mode === 'single') {
        if (pendingRangeDate.from) {
          setSingleDate(pendingRangeDate.from);
          return;
        }

        if (rangeDate.from) {
          setSingleDate(rangeDate.from);
          setRawInputValue(formatDate(rangeDate.from, dateLocale));
          if (withConfirmButtons) {
            setDisplayValue(formatDate(rangeDate.from, dateLocale));
          }
        }
      } else if (mode === 'range') {
        if (pendingSingleDate) {
          setRangeDate({ from: pendingSingleDate, to: pendingSingleDate });
          return;
        }

        if (singleDate) {
          setRangeDate({ from: singleDate, to: singleDate });
          setRawInputValue(
            `${formatDate(singleDate, dateLocale)} - ${formatDate(singleDate, dateLocale)}`
          );
          if (withConfirmButtons) {
            setDisplayValue(
              `${formatDate(singleDate, dateLocale)} - ${formatDate(singleDate, dateLocale)}`
            );
          }
        }
      }
    }, [mode]);
    // Wrapper to trigger onBlur
    const containerRef = React.useRef<HTMLDivElement>(null);

    const handleBlur = () => {
      onBlur?.({} as React.FocusEvent<HTMLInputElement>);
    };

    // Close detection: if click outside input & popover, trigger onBlur
    React.useEffect(() => {
      function handleClickOutside(event: MouseEvent) {
        if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
          handleBlur();
        }
      }

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);
    /*
      Presets - all are in (ms)
    */
    const presetRanges = [
      { label: 'Today', range: { from: new Date(), to: new Date() } },
      {
        label: 'Yesterday',
        range: {
          from: new Date(Date.now() - 24 * 60 * 60 * 1000),
          to: new Date(Date.now() - 24 * 60 * 60 * 1000)
        }
      },
      {
        label: 'Last 7 Days',
        range: { from: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), to: new Date() }
      },
      {
        label: 'Last 30 Days',
        range: { from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), to: new Date() }
      },
      {
        label: 'This Month',
        range: {
          from: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
          to: new Date()
        }
      },
      {
        label: 'Last Month',
        range: {
          from: new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1),
          to: new Date(new Date().getFullYear(), new Date().getMonth(), 0)
        }
      }
    ];

    /*
      Custom style modifiers
      index wise weeks end on saturday hence index[6], lastdayInWeek has the first index, monday second
      All modifiers must have variants for both normal or pending dates, they wont apply if they dont
    */
    const modifiers = React.useMemo(() => {
      if (mode === 'single') {
        return {};
      }

      return {
        rangeStart: withConfirmButtons
          ? pendingRangeDate.from
            ? pendingRangeDate.from
            : rangeDate.from
          : rangeDate.from,
        rangeEnd: withConfirmButtons
          ? pendingRangeDate.to
            ? pendingRangeDate.to
            : rangeDate.to
          : rangeDate.to,
        firstInWeek: (date: Date) =>
          withConfirmButtons
            ? pendingRangeDate.from || pendingRangeDate.to
              ? isDateInRange(date, pendingRangeDate) && date.getDay() === 1
              : isDateInRange(date, rangeDate) && date.getDay() === 1
            : isDateInRange(date, rangeDate) && date.getDay() === 1,
        lastInWeek: (date: Date) =>
          withConfirmButtons
            ? pendingRangeDate.from || pendingRangeDate.to
              ? isDateInRange(date, pendingRangeDate) && date.getDay() === 0
              : isDateInRange(date, rangeDate) && date.getDay() === 0
            : isDateInRange(date, rangeDate) && date.getDay() === 0
      };
    }, [mode, withConfirmButtons, pendingRangeDate, rangeDate]);

    // A wide year range: the dropdown default ends this December, too early for due dates.
    const monthYearSelectProps = () => {
      const today = nowPlainDate();
      return {
        captionLayout: 'dropdown' as const,
        startMonth: toJSDate(
          today.subtract({ years: DROPDOWN_YEARS_BACK }).with({ month: 1, day: 1 })
        ),
        endMonth: toJSDate(today.add({ years: DROPDOWN_YEARS_AHEAD }).with({ month: 12, day: 31 }))
      };
    };

    /*
      Common props for DayPicker both modes, and styling for modifiers, month range application
    */
    const commonDayPickerProps = {
      className: 'm-[12px]',
      modifiers,
      modifiersClassNames: {
        selected:
          'bg-secondary-base  hover:!outline-none text-foreground-white !text-lg' +
          (mode === 'single' ? ' rounded-full' : ' hover:rounded-none mt-xs'),
        hover: 'rounded-full border border-secondary-base',
        range_middle:
          '!bg-background-muted hover:!outline-none hover:rounded-none !text-foreground-base hover:!text-foreground-muted ',
        firstInWeek: 'rounded-l-full hover:!rounded-l-full',
        lastInWeek: 'rounded-r-full hover:!rounded-r-full',
        rangeStart: 'rounded-l-full !bg-background-muted hover:!rounded-l-full',
        rangeEnd: 'rounded-r-full bg-background-muted hover:!rounded-r-full'
      },
      classNames: {
        day: 'hover:outline hover:outline-secondary-base hover:rounded-full',
        table: 'border-separate border-spacing-y-1',
        months:
          MonthRangeConfig === 12
            ? 'grid grid-cols-4 gap-md'
            : MonthRangeConfig === 6
              ? 'grid grid-cols-3 gap-md'
              : MonthRangeConfig === 4
                ? 'grid grid-cols-2 gap-lg'
                : 'flex flex-col sm:flex-row gap-lg' // 1-2 months: stack on mobile, side-by-side on sm+
      },
      numberOfMonths: MonthRangeConfig,
      locale: getDayPickerLocale(dateLocale),
      showOutsideDays: true
    };

    return (
      <Popover>
        {({ open, close }) => (
          <>
            {/*Date input and click  section*/}
            <Input
              id={id}
              disabled={disabled}
              value={rawInputValue}
              className={cn(className, GLOBAL_HEIGHT[height])}
              placeholder={placeholder}
              hasError={hasError}
              onBlur={handleBlur}
              autoFocus={autoFocus}
              testId={testId}
              onChange={e => {
                /*
                This entire block controls the input mechanism
              */

                const value = e.target.value;

                /*
                If the value is empty we also empty out all states and trigger a change
              */
                if (value === '') {
                  if (withConfirmButtons) {
                    if (mode === 'range') {
                      setPendingRangeDate({ from: undefined, to: undefined });
                    } else {
                      setPendingSingleDate(undefined);
                    }
                  } else {
                    if (mode === 'range') {
                      setRangeDate({ from: undefined, to: undefined });
                    } else {
                      setSingleDate(undefined);
                    }
                  }

                  if (mode === 'range') {
                    setRangeDate({ from: undefined, to: undefined });
                    onChange?.({ from: undefined, to: undefined } as RangeDate);
                  } else {
                    setSingleDate(undefined);
                    onChange?.(undefined!);
                  }
                }

                setRawInputValue(value);

                if (mode === 'single') {
                  const parsedDate = tryParseDate(value);
                  if (parsedDate) {
                    if (withConfirmButtons) {
                      setPendingSingleDate(parsedDate);
                    } else {
                      setSingleDate(parsedDate);
                      onChange?.(toPlainDate(parsedDate));
                    }
                  }
                }

                if (mode === 'range') {
                  // Split on RANGE_SEPARATOR (' - ') from the raw value *before* whitespace
                  // stripping. Splitting cleanValue on '-' would break ISO-formatted dates
                  // (e.g. sv-SE "2025-11-19 - 2025-11-20" → cleanValue "2025-11-19-2025-11-20"
                  // → 6 parts instead of 2).
                  const rangeParts = value.split(RANGE_SEPARATOR);

                  if (rangeParts.length === 2) {
                    const fromDate = tryParseDate(rangeParts[0].trim());
                    const toDate = tryParseDate(rangeParts[1].trim());

                    if (fromDate && toDate && fromDate <= toDate) {
                      const newRange: InternalRangeDate = { from: fromDate, to: toDate };

                      if (withConfirmButtons) {
                        setPendingRangeDate(newRange);
                      } else {
                        setRangeDate(newRange);
                        onChange?.(toPublicRange(newRange));
                      }
                    }
                  }
                }
              }}
              onKeyDown={e => {
                // An open calendar puts a focus guard beside its button; closing it first lets
                // Tab and Shift+Tab leave the field in one press.
                if (e.key === 'Tab' && open) flushSync(() => close(inputRef));
                // ArrowDown (or Alt+ArrowDown) opens the calendar, a second one enters its days.
                if (e.key === 'ArrowDown' && !disabled) {
                  e.preventDefault();
                  if (open) focusCalendarDay();
                  else openCalendar();
                  return;
                }
                if (e.key === 'Escape' && open) {
                  e.preventDefault();
                  e.stopPropagation();
                  flushSync(() => close(inputRef));
                  return;
                }
                const allowedKeys = [
                  'Backspace',
                  'Delete',
                  'ArrowLeft',
                  'ArrowRight',
                  'Tab',
                  ' ',
                  '-',
                  '.',
                  '/'
                ];
                if (!/[0-9]/.test(e.key) && !allowedKeys.includes(e.key)) {
                  e.preventDefault();
                }
              }}
              leftContent={
                triggerAnchor === 'left' &&
                !disabled && (
                  <PopoverButton
                    ref={popoverButtonRef}
                    aria-label='Open calendar'
                    // Mouse only, so the field is one tab stop; the date is typed into the input.
                    tabIndex={-1}
                    onClick={() => {
                      if (suppressNextButtonBlur.current) {
                        suppressNextButtonBlur.current = false;
                        return;
                      }
                      onBlur?.({} as unknown as React.FocusEvent<HTMLInputElement>);
                    }}
                    className='flex items-center p-xs rounded focus-visible:!border-none focus-visible:!outline-none focus-visible:!ring-none'
                  >
                    <Icon name='calendar-clock' />
                  </PopoverButton>
                )
              }
              rightContent={
                triggerAnchor === 'right' &&
                !disabled && (
                  <PopoverButton
                    ref={popoverButtonRef}
                    aria-label='Open calendar'
                    // Mouse only, so the field is one tab stop; the date is typed into the input.
                    tabIndex={-1}
                    onClick={() => {
                      if (suppressNextButtonBlur.current) {
                        suppressNextButtonBlur.current = false;
                        return;
                      }
                      onBlur?.({} as unknown as React.FocusEvent<HTMLInputElement>);
                    }}
                    className='flex items-center p-xs rounded focus-visible:!border-none focus-visible:!outline-none focus-visible:!ring-none'
                  >
                    <Icon name='calendar-clock' />
                  </PopoverButton>
                )
              }
              ref={setInputRef}
            />

            <Transition
              enter='transition ease-out duration-200'
              enterFrom='opacity-0 translate-y-1'
              enterTo='opacity-100 translate-y-0'
              leave='transition ease-in duration-150'
              leaveFrom='opacity-100 translate-y-0'
              leaveTo='opacity-0 translate-y-1'
            >
              <PopoverPanel
                ref={panelRef}
                anchor={{ to: 'bottom start', gap: '8px' }}
                onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                  // Tab leaves the field from anywhere in the calendar; Headless UI would look for
                  // the element after the calendar button, which is out of the tab order.
                  if (e.key === 'Tab') {
                    e.preventDefault();
                    const input = inputRef.current;
                    const target =
                      input && adjacentTabbable(input, e.shiftKey ? -1 : 1, e.currentTarget);
                    flushSync(() => close(target ?? inputRef));
                  }
                  // Escape returns to the input, not to the calendar button.
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    e.stopPropagation();
                    flushSync(() => close(inputRef));
                  }
                }}
                className='border border-border-base rounded-lg shadow-lg bg-background-input w-max max-w-[calc(100vw-1rem)] flex flex-col overflow-hidden z-[51]'
              >
                <div className='flex flex-row'>
                  {/*Presets section*/}
                  {withPresets && mode === 'range' && (
                    <div className='flex flex-col gap-md pt-xl ps-lg pr-lg bg-background-muted border-r border-r-border-base'>
                      {presetRanges.map(preset => (
                        <button
                          key={preset.label}
                          onClick={() => {
                            /*
                          Onchange is triggered when set
                        */
                            if (withConfirmButtons) {
                              setPendingRangeDate(preset.range);
                            } else {
                              setRangeDate(preset.range);
                              onChange?.(toPublicRange(preset.range));
                              const formattedValue = `${formatDate(preset.range.from!, dateLocale)} - ${formatDate(preset.range.to, dateLocale)}`;
                              setRawInputValue(formattedValue);
                            }
                          }}
                          className='text-[16px] text-left text-foreground-base hover:text-text-foreground-base transition duration:300'
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>
                  )}

                  {mode === 'single' ? (
                    <DayPicker
                      disabled={[
                        ...(disableBefore ? [{ before: toJSDate(disableBefore) }] : []),
                        ...(disableAfter ? [{ after: toJSDate(disableAfter) }] : [])
                      ]}
                      {...commonDayPickerProps}
                      {...(withMonthYearSelect && monthYearSelectProps())}
                      mode='single'
                      selected={
                        withConfirmButtons
                          ? pendingSingleDate
                            ? pendingSingleDate
                            : singleDate
                          : singleDate
                      }
                      onSelect={date => {
                        if (withConfirmButtons) {
                          setPendingSingleDate(date as Date);
                        } else {
                          setSingleDate(date as Date);
                          setRawInputValue(date ? formatDate(date, dateLocale) : '');
                          onChange?.(date ? toPlainDate(date) : undefined);
                        }
                      }}
                    />
                  ) : (
                    <DayPicker
                      {...commonDayPickerProps}
                      mode='range'
                      selected={
                        withConfirmButtons
                          ? pendingRangeDate.from || pendingRangeDate.to
                            ? asDateRange(pendingRangeDate)
                            : asDateRange(rangeDate)
                          : asDateRange(rangeDate)
                      }
                      onSelect={value => {
                        if (withConfirmButtons) {
                          if (!value || !value.from) {
                            setRangeDate({ from: undefined, to: undefined });
                            setPendingRangeDate({ from: undefined, to: undefined });
                            setRawInputValue('');
                            setDisplayValue('');
                            onChange?.({ from: undefined, to: undefined } as RangeDate);
                          } else {
                            setPendingRangeDate(value as InternalRangeDate);
                          }
                        } else {
                          if (!value || !value.from) {
                            setRangeDate({ from: undefined, to: undefined });
                            setRawInputValue('');
                            onChange?.({ from: undefined, to: undefined } as RangeDate);
                          } else {
                            const fromDate = value.from;
                            const toDate = value.to;

                            const formattedValue = toDate
                              ? `${formatDate(fromDate, dateLocale)} - ${formatDate(toDate, dateLocale)}`
                              : `${formatDate(fromDate, dateLocale)}`;

                            setRangeDate(value as InternalRangeDate);
                            setRawInputValue(formattedValue);
                            onChange?.(toPublicRange(value as InternalRangeDate));
                          }
                        }
                      }}
                      required={false}
                    />
                  )}
                </div>
                {/*Confirmation section*/}
                {withConfirmButtons && (
                  <div className='flex justify-end items-center gap-sm p-md border-t border-t-border-base'>
                    <p className='foreground-muted mr-sm'>{displayValue}</p>
                    <button
                      onClick={() => {
                        if (mode === 'single') {
                          if (rawInputValue !== '' && singleDate === undefined) {
                            setRawInputValue('');
                            setPendingSingleDate(undefined);
                            if (closeOnConfirm) handleConfirm();
                            return;
                          }

                          if (pendingSingleDate) {
                            setPendingSingleDate(undefined);
                            if (closeOnConfirm && !singleDate) handleConfirm();
                          } else if (singleDate) {
                            setSingleDate(undefined);
                            setRawInputValue('');
                            setDisplayValue('');
                            if (closeOnConfirm) handleConfirm();
                          }
                        } else {
                          if ((rawInputValue !== '' && !rangeDate.from) || !rangeDate.to) {
                            setRawInputValue('');
                            setPendingRangeDate({ from: undefined, to: undefined });
                            if (closeOnConfirm) handleConfirm();
                            return;
                          }

                          if (pendingRangeDate.from || pendingRangeDate.to) {
                            setPendingRangeDate({ from: undefined, to: undefined });
                          } else if (rangeDate.from || rangeDate.to) {
                            setRangeDate({ from: undefined, to: undefined });
                            setRawInputValue('');
                            setDisplayValue('');
                            if (closeOnConfirm) handleConfirm();
                          }
                        }
                      }}
                      className='px-sm py-xs font-medium text-sm border rounded-md text-foreground-base border-2 border-border-base'
                    >
                      {t('cancel')}
                    </button>
                    <button
                      onClick={() => {
                        if (mode === 'single') {
                          if (pendingSingleDate) setSingleDate(pendingSingleDate);
                          setRawInputValue(
                            pendingSingleDate
                              ? formatDate(pendingSingleDate, dateLocale)
                              : singleDate
                                ? formatDate(singleDate, dateLocale)
                                : ''
                          );
                          setDisplayValue(
                            pendingSingleDate
                              ? formatDate(pendingSingleDate, dateLocale)
                              : singleDate
                                ? formatDate(singleDate, dateLocale)
                                : ''
                          );

                          onChange?.(
                            pendingSingleDate ? toPlainDate(pendingSingleDate) : undefined!
                          );
                          setPendingSingleDate(undefined);
                        } else {
                          if (pendingRangeDate.from || pendingRangeDate.to)
                            setRangeDate(pendingRangeDate);
                          const fromDate = pendingRangeDate.from
                            ? pendingRangeDate.from
                            : rangeDate.from;
                          const toDate = pendingRangeDate.to ? pendingRangeDate.to : rangeDate.to;

                          const formattedValue = fromDate
                            ? toDate
                              ? `${formatDate(fromDate, dateLocale)} - ${formatDate(toDate, dateLocale)}`
                              : `${formatDate(fromDate, dateLocale)}`
                            : '';

                          setRawInputValue(formattedValue);
                          setDisplayValue(formattedValue);
                          onChange?.(toPublicRange(pendingRangeDate));
                          setPendingRangeDate({ from: undefined, to: undefined });
                        }
                        if (closeOnConfirm) handleConfirm();
                      }}
                      className='px-sm py-xs text-sm text-foreground-white border rounded-md bg-primary-base'
                    >
                      {t('confirm')}
                    </button>
                  </div>
                )}
              </PopoverPanel>
            </Transition>
          </>
        )}
      </Popover>
    );
  }
);

Datepicker.displayName = 'Datepicker';
